/**
 * The editor of an own template (D10): subject, the placeholder catalogue,
 * the body, and the way to the review.
 *
 * NO SAVE HERE. The editor ends in "Änderungen prüfen" — the review is the
 * only place a version is stored — and "Änderungen verwerfen", which asks
 * first. "Änderungen prüfen" is disabled while nothing differs from the stored
 * template, and while the draft's preview is being made for the review.
 *
 * PROBLEMS BEFORE REQUESTS. What the editor can tell from the server's limits
 * alone (an empty subject, a subject over the limit, an empty body) is named
 * here, before a preview or a write is spent on it.
 *
 * The state lives in the tab; this component lays it out and reports back.
 */
import React from "react";

import AutomationTemplateBodyEditor from "./AutomationTemplateBodyEditor";
import AutomationTemplateField from "./AutomationTemplateField";
import AutomationTemplatePlaceholders from "./AutomationTemplatePlaceholders";
import { draftProblemLabel } from "./automationTemplateLabels";

const PRIMARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-primary bg-primary px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const SECONDARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const LABEL = "text-[12.5px] font-semibold text-foreground";

export default function AutomationTemplateEditor({
  draft,
  limits,
  subjectLength,
  fieldPropsFor,
  placeholders,
  canInsert,
  onInsertPlaceholder,
  bodyEditorProps,
  problems,
  canDiscard,
  onDiscard,
  reviewDisabled,
  reviewPending,
  onReview,
}) {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className={LABEL}>Betreff</span>
          {limits.max_subject_chars ? (
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {subjectLength} / {limits.max_subject_chars} Zeichen
            </span>
          ) : null}
        </div>
        <AutomationTemplateField
          {...fieldPropsFor("subject")(draft.subject)}
          paragraph={draft.subject}
          label="Betreff"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>Platzhalter</span>
        <AutomationTemplatePlaceholders
          placeholders={placeholders}
          canInsert={canInsert}
          onInsert={onInsertPlaceholder}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>Inhalt</span>
        <AutomationTemplateBodyEditor
          body={draft.body}
          fieldProps={fieldPropsFor("body")}
          {...bodyEditorProps}
        />
      </div>

      {problems.length ? (
        <ul
          role="alert"
          className="m-0 flex flex-col gap-0.5 rounded-[10px] border border-destructive/40 px-3 py-2 pl-7 text-[12px] text-destructive"
        >
          {problems.map((code) => (
            <li key={code}>{draftProblemLabel(code, limits)}</li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        {canDiscard ? (
          <button type="button" onClick={onDiscard} className={SECONDARY_BUTTON}>
            Änderungen verwerfen
          </button>
        ) : null}
        <button
          type="button"
          onClick={onReview}
          disabled={reviewDisabled}
          className={PRIMARY_BUTTON}
        >
          {reviewPending ? "Vorschau wird erstellt …" : "Änderungen prüfen"}
        </button>
      </div>
    </div>
  );
}
